import { ChangeEvent } from "react";
import { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { PricebookValidity } from "../../../../types/price-book.type";
import { todayInBangkok } from "../../../../utils/date";
import { SectionTitle, inputSx } from "../../shared/FormField";
import { PriceBookFormErrors } from "../types";
import { buildFacePriceFallbackNote } from "../validityModel";
import {
  EFFECTIVE_MODES,
  EFFECTIVE_MODE_HINT,
  EFFECTIVE_MODE_LABEL,
  ValidityForm,
  resolveFromMinDate,
  toEffectiveModeValue,
  toRequestedValidity,
} from "./validityForm";

const PICKER_FORMAT = "DD/MM/YYYY";

interface ValidityFieldsProps {
  form: ValidityForm;
  errors: PriceBookFormErrors;
  disabled: boolean;
  /** The book's validity on an edit (its first day may stay although it has passed); null otherwise. */
  storedValidity: PricebookValidity | null;
  onChange: (changes: Partial<ValidityForm>) => void;
}

/**
 * When the book prices: every day from the save (ทันที), from a later day on (ล่วงหน้า), or only
 * between two days (ตามช่วงวันที่). Outside them its shops bill at the face price.
 */
export default function ValidityFields({
  form,
  errors,
  disabled,
  storedValidity,
  onChange,
}: ValidityFieldsProps) {
  const today = todayInBangkok();
  const fromMinDate = resolveFromMinDate(form.effectiveMode, today, storedValidity);
  const toMinDate =
    form.effectiveFrom?.isValid() && form.effectiveFrom.isAfter(today, "day")
      ? form.effectiveFrom
      : today;
  const hasDays = form.effectiveMode !== "now";
  const fallbackNote = buildFacePriceFallbackNote(toRequestedValidity(form), undefined);

  const handleChangeMode = (_event: ChangeEvent<HTMLInputElement>, value: string) => {
    const effectiveMode = toEffectiveModeValue(value);
    if (effectiveMode) onChange({ effectiveMode });
  };
  const handleChangeFrom = (effectiveFrom: Dayjs | null) => onChange({ effectiveFrom });
  const handleChangeTo = (effectiveTo: Dayjs | null) => onChange({ effectiveTo });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
      <SectionTitle>วันที่ใช้ได้</SectionTitle>
      <RadioGroup row value={form.effectiveMode} onChange={handleChangeMode}>
        {EFFECTIVE_MODES.map((mode) => (
          <FormControlLabel
            key={mode}
            value={mode}
            control={<Radio size="small" />}
            disabled={disabled}
            label={EFFECTIVE_MODE_LABEL[mode]}
          />
        ))}
      </RadioGroup>
      <Typography variant="caption" color="text.secondary" component="p" sx={inputSx}>
        {EFFECTIVE_MODE_HINT[form.effectiveMode]}
      </Typography>
      {hasDays && (
        <Stack direction={{ xs: "column", sm: "row" }} gap={2} sx={inputSx}>
          <DatePicker
            label="วันเริ่มใช้"
            value={form.effectiveFrom}
            format={PICKER_FORMAT}
            minDate={fromMinDate}
            disabled={disabled}
            onChange={handleChangeFrom}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                error: Boolean(errors.effectiveFrom),
                helperText: errors.effectiveFrom,
              },
            }}
          />
          {form.effectiveMode === "range" && (
            <DatePicker
              label="วันสิ้นสุด"
              value={form.effectiveTo}
              format={PICKER_FORMAT}
              minDate={toMinDate}
              disabled={disabled}
              onChange={handleChangeTo}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  error: Boolean(errors.effectiveTo),
                  helperText: errors.effectiveTo,
                },
              }}
            />
          )}
        </Stack>
      )}
      {hasDays && (
        <Typography variant="caption" color="text.secondary" component="p" sx={inputSx}>
          {fallbackNote}
        </Typography>
      )}
    </LocalizationProvider>
  );
}
